import { useState, useEffect, useRef, useCallback } from 'react';
import { 
  getSubscriberProfile, 
  getBankDetails, 
  getRedemptionHistory,
  approveRedemptionRequest
} from '../services/subscriberWalletService';
import { getCurrentRole } from '../utils/redemptionHelpers';

const REDEMPTION_PAGE_SIZE = 25;

export const useRedemptionData = (id) => {
  const [profile, setProfile] = useState(null);
  const [bank, setBank] = useState(null);
  const [redemptions, setRedemptions] = useState([]);
  const [redemptionPagination, setRedemptionPagination] = useState({ page: 1, items: REDEMPTION_PAGE_SIZE, total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [redemptionInitialLoadComplete, setRedemptionInitialLoadComplete] = useState(false);
  const isLoadingDataRef = useRef(false);
  
  const currentRole = getCurrentRole();
  
  // Load initial data
  useEffect(() => {
    if (!id || !currentRole || isLoadingDataRef.current) {
      return;
    }
    
    isLoadingDataRef.current = true;
    
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const timeoutId = setTimeout(() => {
          setError('API request timeout. Please check your connection and try again.');
        }, 30000);
        
        const [profileData, bankData, redemptionsData] = await Promise.allSettled([
          getSubscriberProfile(id, currentRole).catch(err => {
            return { error: 'Profile API failed', details: err.message };
          }),
          getBankDetails(id, currentRole).catch(err => {
            return { error: 'Bank API failed', details: err.message };
          }),
          getRedemptionHistory(id, currentRole, { page: 1, items: REDEMPTION_PAGE_SIZE }).catch(err => {
            return { error: 'Redemptions API failed', details: err.message };
          })
        ]);
        
        // Handle profile data
        if (profileData.status === 'fulfilled' && profileData.value) {
          const response = profileData.value;
          let profileToSet = null;
          
          if (response.msg && typeof response.msg === 'object') {
            profileToSet = response.msg;
          } else if (response.subscription || response.valid_period || response.plan) {
            profileToSet = response;
          } else if (response.data && typeof response.data === 'object') {
            profileToSet = response.data;
          } else if (response.success && response.data && typeof response.data === 'object') {
            profileToSet = response.data;
          } else {
            profileToSet = {
              id: response.id || id,
              personal: { 
                full_name: response.full_name || response.name || response.first_name || '', 
                mobile: response.mobile || response.phone || response.phone_number || '' 
              },
              subscription: response.subscription || response.subscription_id || '',
              valid_period: response.valid_period || response.period || response.validity_period || '',
              plan: response.plan || response.plan_name || '',
              status: response.status || response.active || '',
              kyc: response.kyc || response.kyc_status || ''
            };
          }
          
          if (profileToSet) {
            setProfile(profileToSet);
          }
        } else if (profileData.status === 'rejected' || (profileData.value && profileData.value.error)) {
          setProfile({
            id: id,
            personal: { full_name: 'Error Loading', mobile: 'Error Loading' },
            subscription: 'Error Loading',
            valid_period: 'Error Loading',
            plan: 'Error Loading',
            status: 'Error Loading',
            kyc: 'Error Loading'
          });
        } else {
          setProfile({
            id: id,
            personal: { full_name: '', mobile: '' },
            subscription: '',
            valid_period: '',
            plan: '',
            status: '',
            kyc: ''
          });
        }
        
        // Handle bank data
        if (bankData.status === 'fulfilled' && bankData.value) {
          const response = bankData.value;
          if (response.msg && typeof response.msg === 'object') {
            setBank(response.msg);
          } else if (response.data && typeof response.data === 'object') {
            setBank(response.data);
          } else {
            setBank(null);
          }
        } else {
          setBank(null);
        }
        
        // Handle redemptions data
        if (redemptionsData.status === 'fulfilled' && redemptionsData.value) {
          const response = redemptionsData.value;
          if (response.metadata && response.metadata.api_status === false) {
            setRedemptions([]);
            setRedemptionPagination({ page: 1, items: REDEMPTION_PAGE_SIZE, total: 0 });
          } else if (response.msg && response.msg.items && Array.isArray(response.msg.items)) {
            setRedemptions(response.msg.items);
            setRedemptionPagination(response.msg.pagination || { page: 1, items: REDEMPTION_PAGE_SIZE, total: response.msg.items.length });
          } else if (response.data && response.data.items && Array.isArray(response.data.items)) {
            setRedemptions(response.data.items);
            setRedemptionPagination(response.data.pagination || { page: 1, items: REDEMPTION_PAGE_SIZE, total: response.data.items.length });
          } else if (Array.isArray(response)) {
            setRedemptions(response);
            setRedemptionPagination({ page: 1, items: REDEMPTION_PAGE_SIZE, total: response.length });
          } else {
            setRedemptions([]);
            setRedemptionPagination({ page: 1, items: REDEMPTION_PAGE_SIZE, total: 0 });
          }
        } else {
          setRedemptions([]);
          setRedemptionPagination({ page: 1, items: REDEMPTION_PAGE_SIZE, total: 0 });
        }
        
        setRedemptionInitialLoadComplete(true);
        clearTimeout(timeoutId);
      } catch (error) {
        console.error('Error loading data:', error);
        setError('Failed to load subscriber data');
      } finally {
        setLoading(false);
        isLoadingDataRef.current = false;
      }
    };
    
    loadData();
  }, [id, currentRole]);
  
  return {
    profile,
    bank,
    redemptions,
    redemptionPagination,
    loading,
    error,
    currentRole,
    redemptionInitialLoadComplete,
    setRedemptions,
    setRedemptionPagination
  };
};

export const useRedemptionHistory = (id, currentRole, redemptionPage, redemptionSearch, redemptionInitialLoadComplete, redemptionStatus) => {
  const [redemptions, setRedemptions] = useState([]);
  const [redemptionPagination, setRedemptionPagination] = useState({ page: 1, items: REDEMPTION_PAGE_SIZE, total: 0 });

  useEffect(() => {
    if (!redemptionInitialLoadComplete || !id || !currentRole) {
      return;
    }

    const loadRedemptionHistory = async () => {
      try {
        const params = {
          page: redemptionPage,
          items: REDEMPTION_PAGE_SIZE,
          ...(redemptionSearch && { search: redemptionSearch }),
          ...(redemptionStatus && redemptionStatus !== 'all' && { status: redemptionStatus })
        };

        const response = await getRedemptionHistory(id, currentRole, params);

        if (response && response.metadata && response.metadata.api_status === false) {
          setRedemptions([]);
          setRedemptionPagination({ page: 1, items: REDEMPTION_PAGE_SIZE, total: 0 });
        } else if (response && response.msg && response.msg.items && Array.isArray(response.msg.items)) {
          setRedemptions(response.msg.items);
          setRedemptionPagination(response.msg.pagination || {
            page: redemptionPage,
            items: REDEMPTION_PAGE_SIZE,
            total: response.msg.items.length,
            total_pages: Math.ceil(response.msg.items.length / REDEMPTION_PAGE_SIZE)
          });
        } else if (response && response.data && response.data.items && Array.isArray(response.data.items)) {
          setRedemptions(response.data.items);
          setRedemptionPagination(response.data.pagination || {
            page: redemptionPage,
            items: REDEMPTION_PAGE_SIZE,
            total: response.data.items.length,
            total_pages: Math.ceil(response.data.items.length / REDEMPTION_PAGE_SIZE)
          });
        } else if (Array.isArray(response)) {
          setRedemptions(response);
          setRedemptionPagination({
            page: redemptionPage,
            items: REDEMPTION_PAGE_SIZE,
            total: response.length,
            total_pages: Math.ceil(response.length / REDEMPTION_PAGE_SIZE)
          });
        } else {
          setRedemptions([]);
          setRedemptionPagination({ page: 1, items: REDEMPTION_PAGE_SIZE, total: 0 });
        }
      } catch (error) {
        console.error('Error loading redemption history:', error);
        setRedemptions([]);
        setRedemptionPagination({ page: 1, items: REDEMPTION_PAGE_SIZE, total: 0 });
      }
    };

    loadRedemptionHistory();
  }, [id, currentRole, redemptionPage, redemptionSearch, redemptionStatus, redemptionInitialLoadComplete]);

  return { redemptions, redemptionPagination, setRedemptions, setRedemptionPagination };
};

export const useApproveRedemption = (id, currentRole, redemptionPage, redemptionSearch, setRedemptions, setRedemptionPagination) => {
  const [approvingRequests, setApprovingRequests] = useState(new Set());
  const [approvedRequestIds, setApprovedRequestIds] = useState(new Set());
  
  const handleApproveRequest = useCallback(async (requestId) => {
    if (!requestId || !currentRole) return;
    
    if (currentRole !== 'Finance' && currentRole !== 'SuperAdmin') {
      return { success: false, message: 'You do not have permission to approve redemption requests.' };
    }
    
    setApprovingRequests(prev => new Set(prev).add(requestId));
    
    try {
      const response = await approveRedemptionRequest(currentRole, requestId);
      
      if (response && response.metadata && response.metadata.api_status === true) {
        setApprovedRequestIds(prev => {
          const newSet = new Set(prev);
          newSet.add(requestId);
          return newSet;
        });
        
        // Refresh redemption history
        const params = {
          page: redemptionPage,
          items: REDEMPTION_PAGE_SIZE,
          ...(redemptionSearch && { search: redemptionSearch })
        };
        
        const refreshResponse = await getRedemptionHistory(id, currentRole, params);
        
        if (refreshResponse && refreshResponse.metadata && refreshResponse.metadata.api_status) {
          if (refreshResponse.msg && refreshResponse.msg.items && Array.isArray(refreshResponse.msg.items)) {
            setRedemptions(refreshResponse.msg.items);
            if (refreshResponse.msg.pagination) {
              setRedemptionPagination(refreshResponse.msg.pagination);
            }
          } else if (refreshResponse.data && refreshResponse.data.items && Array.isArray(refreshResponse.data.items)) {
            setRedemptions(refreshResponse.data.items);
            if (refreshResponse.data.pagination) {
              setRedemptionPagination(refreshResponse.data.pagination);
            }
          } else if (Array.isArray(refreshResponse)) {
            setRedemptions(refreshResponse);
          }
        }
        
        return { success: true, message: 'Redemption request approved successfully!' };
      } else {
        const errorMessage = response?.msg?.error || 'Failed to approve redemption request';
        const errorDetails = response?.msg?.details || {};
        
        if (errorMessage.includes('not found') || errorMessage.includes('already processed')) {
          return { success: false, message: 'Redemption request not found or already processed' };
        } else if (errorDetails.actioned_at && Array.isArray(errorDetails.actioned_at)) {
          return { success: false, message: errorDetails.actioned_at[0] || errorMessage };
        } else {
          return { success: false, message: errorMessage };
        }
      }
    } catch (error) {
      console.error('Network error:', error);
      return { success: false, message: 'Network error. Please try again.' };
    } finally {
      setApprovingRequests(prev => {
        const newSet = new Set(prev);
        newSet.delete(requestId);
        return newSet;
      });
    }
  }, [id, currentRole, redemptionPage, redemptionSearch, setRedemptions, setRedemptionPagination]);
  
  return { handleApproveRequest, approvingRequests, approvedRequestIds };
};

